import { useClient, useMutation } from '@weenat/client'
import useDisplayParams from '@weenat/client/dist/hooks/useDisplayParams'
import { DisplayPreferences } from '@weenat/client/dist/resources/preferences'
import { useIntl } from '@weenat/wintl'
import { useSearchParams } from 'app/routx-router'
import useDisclosure from 'app/src/hooks/useDisclosure'
import Icons from 'app/src/kit/Icons'
import Link from 'app/src/kit/LinkComponent'
import PillTabs from 'app/src/kit/PillTabs'
import Text from 'app/src/kit/Text'
import {
  Dropdown,
  DropdownContent,
  DropdownTrigger
} from 'app/src/kit/fields/Select/SelectDropdown'
import { TogglePrimitive } from 'app/src/kit/fields/ToggleField'
import PanelSection from 'app/src/kit/panels/PanelSection'
import { Tooltip, TooltipContent, TooltipTrigger } from 'app/src/kit/tooltips/Tooltip'
import { displaySettingsSearchParamsSchema, useClusterSettings, useToggleFeature } from 'app/state'
import { styled } from 'styled-components'
const SelectedFilterBox = styled(Box)`
  border: 0px solid ${(p) => p.theme.colors.grayscale[300]};
  border-left-width: 1px;
  padding: ${(p) => p.theme.spacings.md}px;
`

const ToggleSelector = ({
  label,
  iconKey,
  value,
  toggleAction,
  isDisabled
}: {
  label: string
  iconKey: keyof typeof Icons
  isDisabled: boolean
  value: boolean
  toggleAction: () => void
}) => {
  const Icon = Icons[iconKey]
  return (
    <Flex $alignItems='center' $justifyContent='space-between' $gap='md'>
      <Flex $alignItems='center' $gap='md'>
        <Icon $size='lg' />
        <Text $lineHeight='none'>{label}</Text>
      </Flex>
      <TogglePrimitive $isDisabled={isDisabled} value={value} onToggle={toggleAction} />
    </Flex>
  )
}

const DisplaySettings: FC<{ isWeatherMap?: boolean }> = ({ isWeatherMap = false }) => {
  const { t } = useIntl()
  const client = useClient()
  const { isOpen, toggle, close } = useDisclosure()
  const [searchParams, setSearchParams] = useSearchParams(displaySettingsSearchParamsSchema)

  const [{ on_nav_favorites }] = useToggleFeature()
  const [clusterSettings, setClusterSettings] = useClusterSettings()
  const displayPreferences = useDisplayParams()

  const { onlyFavorites, hideEmptyDataPOD, hidePlots, hideStations, hideVirtualWeatherStations } =
    displayPreferences

  const hasFilterActive =
    hidePlots || hideStations || hideVirtualWeatherStations || onlyFavorites || hideEmptyDataPOD

  const [updatePreferences, updatePreferencesRequest] = useMutation(client.preferences.update())

  const handleDispatch = (displayParam: Partial<DisplayPreferences>) => {
    updatePreferences({
      display: { ...displayPreferences, ...displayParam }
    })
  }

  if (isWeatherMap) {
    return (
      <Dropdown
        isVisible={isOpen}
        onVisibleChange={() => (isOpen ? close() : open())}
        marginFromParent={16}
      >
        <DropdownTrigger>
          <Flex
            $alignItems='center'
            $gap='md'
            $backgroundColor='grayscale.white'
            $color='grayscale.black'
            $borderColor={'grayscale.300'}
            $outlineColor={isOpen ? 'primary.500' : 'transparent'}
            $outlineWidth={2}
            $outlineOffset={0}
            $borderRadius='sm'
            $pl={'md'}
            $px={'md'}
            $borderWidth={1}
            onClick={toggle}
            $cursor='pointer'
          >
            <Flex $flex={1} $alignItems='center' $gap='md' $py='sm' $my='xs'>
              <Icons.Tune $size='lg' />
              <Text $fontWeight='bold'>{t('map_settings.view.title')}</Text>
            </Flex>
          </Flex>
        </DropdownTrigger>
        <DropdownContent width={480}>
          <PanelSection title={t('map.views.options_title')}>
            <Tooltip disabled={!clusterSettings.enabled} placement='bottom-start'>
              <TooltipTrigger>
                <ToggleSelector
                  label={t('map.display_clusters')}
                  iconKey='GroupWork'
                  isDisabled={false}
                  value={clusterSettings.enabled}
                  toggleAction={() =>
                    setClusterSettings({
                      ...clusterSettings,
                      enabled: !clusterSettings.enabled
                    })
                  }
                />
              </TooltipTrigger>
              <TooltipContent>
                <Flex $alignItems='flex-start' $gap='md' $width={400}>
                  <Box>
                    <Icons.AlertTriangle $size='lg' $color='grayscale.white' />
                  </Box>
                  <Text $color='grayscale.white'>{t('map.display_clusters_warning')}</Text>
                </Flex>
              </TooltipContent>
            </Tooltip>
          </PanelSection>
        </DropdownContent>
      </Dropdown>
    )
  }

  return (
    <Dropdown
      isVisible={isOpen}
      onVisibleChange={() => (isOpen ? close() : open())}
      marginFromParent={16}
    >
      <DropdownTrigger>
        <Flex
          $alignItems='center'
          $gap='md'
          $backgroundColor='grayscale.white'
          $color='grayscale.black'
          $borderColor={'grayscale.300'}
          $outlineColor={isOpen ? 'primary.500' : 'transparent'}
          $outlineWidth={2}
          $outlineOffset={0}
          $borderRadius='sm'
          $pl={hasFilterActive ? 'md' : undefined}
          $px={!hasFilterActive ? 'md' : undefined}
          $borderWidth={1}
          onClick={toggle}
          $cursor='pointer'
        >
          <Flex $flex={1} $alignItems='center' $gap='md' $py='sm' $my='xs'>
            <Icons.Tune $size='lg' />
            <Text $fontWeight='bold'>{t('map_settings.view.title')}</Text>
          </Flex>
          {hasFilterActive ? (
            <Flex $alignItems='center' $ml='md'>
              {hidePlots ? (
                <SelectedFilterBox>
                  <Icons.PlotsFilledOff $size='md' className='display-settings-filter-icon' />
                </SelectedFilterBox>
              ) : null}
              {hideStations ? (
                <SelectedFilterBox>
                  <Icons.SingleSensorFilledOff
                    $size='md'
                    className='display-settings-filter-icon'
                  />
                </SelectedFilterBox>
              ) : null}
              {hideVirtualWeatherStations ? (
                <SelectedFilterBox>
                  <Icons.SingleVirtualSensorFilledOff
                    $size='md'
                    className='display-settings-filter-icon'
                  />
                </SelectedFilterBox>
              ) : null}
              {hideEmptyDataPOD ? (
                <SelectedFilterBox>
                  <Icons.ExploreNearbyOff $size='md' className='display-settings-filter-icon' />
                </SelectedFilterBox>
              ) : null}
              {onlyFavorites && !on_nav_favorites ? (
                <SelectedFilterBox>
                  <Icons.Star $size='md' className='display-settings-filter-icon' />
                </SelectedFilterBox>
              ) : null}
            </Flex>
          ) : null}
        </Flex>
      </DropdownTrigger>
      <DropdownContent width={480}>
        <PanelSection title={t('map.views.label')}>
          <PillTabs
            options={[
              { label: t('map.views.map'), value: 'map' },
              { label: t('map.views.list'), value: 'list' }
            ]}
            value={searchParams.view ?? 'map'}
            onChange={(newView) => setSearchParams({ view: newView })}
            $selectedColor={'grayscale.black'}
            $isCentered={false}
          />
        </PanelSection>
        <PanelSection title={t('map.views.elements_title')}>
          <ToggleSelector
            label={t('map.display_plots')}
            iconKey='PlotsFilled'
            isDisabled={updatePreferencesRequest.isPending}
            value={!hidePlots}
            toggleAction={() => handleDispatch({ hidePlots: !hidePlots })}
          />
          <ToggleSelector
            label={t('map.display_stations')}
            iconKey='SingleSensorFilled'
            isDisabled={updatePreferencesRequest.isPending}
            value={!hideStations}
            toggleAction={() => handleDispatch({ hideStations: !hideStations })}
          />
          <ToggleSelector
            label={t('map.display_virtual_weather_stations')}
            iconKey='SingleVirtualSensorFilled'
            isDisabled={updatePreferencesRequest.isPending}
            value={!hideVirtualWeatherStations}
            toggleAction={() =>
              handleDispatch({ hideVirtualWeatherStations: !hideVirtualWeatherStations })
            }
          />
        </PanelSection>
        <PanelSection title={t('map.views.options_title')}>
          <Flex $flexDirection='column' $gap='lg'>
            <ToggleSelector
              label={t('map.only_with_data')}
              iconKey='ExploreNearbyOff'
              isDisabled={updatePreferencesRequest.isPending}
              value={hideEmptyDataPOD}
              toggleAction={() => handleDispatch({ hideEmptyDataPOD: !hideEmptyDataPOD })}
            />
            {!on_nav_favorites ? (
              <ToggleSelector
                label={t('map.only_favorite')}
                iconKey='Star'
                isDisabled={updatePreferencesRequest.isPending}
                value={onlyFavorites}
                toggleAction={() => handleDispatch({ onlyFavorites: !onlyFavorites })}
              />
            ) : null}
            <Tooltip disabled={!clusterSettings.enabled} placement='bottom-start'>
              <TooltipTrigger>
                <ToggleSelector
                  label={t('map.display_clusters')}
                  iconKey='GroupWork'
                  isDisabled={false}
                  value={clusterSettings.enabled}
                  toggleAction={() =>
                    setClusterSettings({ ...clusterSettings, enabled: !clusterSettings.enabled })
                  }
                />
              </TooltipTrigger>
              <TooltipContent>
                <Flex $alignItems='flex-start' $gap='md' $width={400}>
                  <Box>
                    <Icons.AlertTriangle $size='lg' $color='grayscale.white' />
                  </Box>
                  <Text $color='grayscale.white'>{t('map.display_clusters_warning')}</Text>
                </Flex>
              </TooltipContent>
            </Tooltip>
          </Flex>
        </PanelSection>

        <Flex $justifyContent='center' $p='lg'>
          <Link $underlined to='plots/archived'>
            {t('map.display_archived_plot')}
          </Link>
        </Flex>
      </DropdownContent>
    </Dropdown>
  )
}

export default DisplaySettings
